import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ConfirmToast, { ConfirmInput } from "../../notification/Confirm";
import {
  IDeviceSecretIssued,
  IDeviceSecretRevoked,
  IDeviceSecretStatus,
} from "../../../models/IUser";

interface UseModuleDeviceSecretOptions {
  moduleId?: number;
  /** Только root: иначе ни признак выдачи не читаем, ни операции не зовём. */
  enabled: boolean;
  /**
   * `п. 1` резерва (пара к п. 19): идентификатор изделия (`charge_id`). Его
   * человек вводит при перевыпуске — так подтверждение становится осознанным, а
   * не «да» вслепую.
   */
  chargeId?: string | null;
  fetchStatus?: (moduleId: number) => Promise<IDeviceSecretStatus>;
  issue?: (
    moduleId: number,
    options?: { replace?: boolean },
  ) => Promise<IDeviceSecretIssued>;
  revoke?: (moduleId: number) => Promise<IDeviceSecretRevoked>;
}

/**
 * `п. 1`: сервер ответил `409` — секрет уже выдан, и молчаливой замены нет.
 * Текст называет, что делать: подтвердить перевыпуск.
 */
export const REISSUE_REQUIRED_MESSAGE =
  "секрет уже выдан — подтвердите перевыпуск";

function isConflict(error: any): boolean {
  return error?.response?.status === 409;
}

/** Сообщение об ошибке в том же виде, что у перезагрузки модуля. */
function errorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

/** Успех и отказ показываем в том же тосте, что и подтверждение. */
const RESULT_TOAST = {
  autoClose: 5000,
  closeButton: true,
  closeOnClick: true,
  draggable: true,
  progressClassName: undefined,
} as const;

/**
 * `DEV-97.1` (`АУД-120`, С): исход отзыва секрета виден человеку — и различается
 * явно.
 *
 * Сервер отдаёт `connectionOutcome` тремя значениями: `closed` — шлюз закрыл сокет
 * изделия, связь пропала сейчас; `not_connected` — закрывать было нечего, связь
 * пропадёт сама, когда изделие придёт со старым секретом; `failed` — закрыть не
 * удалось, и изделие **может оставаться на связи** до переподключения: это повод
 * перезапустить его или повторить, а не успокаивающее «не было на связи».
 *
 * Прежде состояний было два (`connectionClosed`), и отказ шлюза читался как «не
 * было на связи» — карточка успокаивала там, где требовалось действие.
 *
 * Без `connectionOutcome` (сборка со старым сервером) читается прежний
 * `connectionClosed`: `true` — «закрыто», иначе — «не было на связи».
 */
export function revokeResultMessage(result: IDeviceSecretRevoked): string {
  return secretOutcomeMessage(result, "отозван");
}

/**
 * Сообщение о закрытии соединения для перевыпуска: та же причина, другая операция.
 */
export function issueResultMessage(result: IDeviceSecretIssued): string {
  return secretOutcomeMessage(result, "перевыпущен");
}

/** Причина отказа в виде, пригодном для строки тоста. */
function closeReason(result: {
  connectionCloseReason?: string | null;
}): string | null {
  const reason = result?.connectionCloseReason;

  return typeof reason === "string" && reason.trim() !== "" ? reason.trim() : null;
}

/** Исход закрытия соединения: `failed` — отказ, изделие может остаться на связи. */
export function connectionOutcomeOf(
  result: IDeviceSecretRevoked | IDeviceSecretIssued,
): "closed" | "not_connected" | "failed" {
  return (
    result?.connectionOutcome ??
    // Прежний сервер: `connectionClosed` ничего не говорит про отказ шлюза,
    // поэтому `false` читается как «закрывать было нечего».
    (result?.connectionClosed ? "closed" : "not_connected")
  );
}

/** Исход закрытия, названный словом; `null` — исход не назван вовсе. */
function outcomeByText(
  result: IDeviceSecretRevoked | IDeviceSecretIssued,
  outcome: "closed" | "not_connected" | "failed",
): string | null {
  if (outcome === "closed") return "соединение изделия закрыто";
  if (outcome === "not_connected") return "изделие не было на связи";

  return null;
}

/**
 * `DEV-97.1` (попутное `АУД-122`): отказ закрытия — сообщение, которое требует
 * действия, и начинается оно с того, что случилось, а не с исхода операции:
 * «Закрыть соединение не удалось — изделие может оставаться на связи до
 * переподключения». Причина приходит с сервера (`connectionCloseReason`) уже
 * по-русски; если её нет, сообщение остаётся полным и без неё.
 */
function failedOutcomeMessage(
  result: IDeviceSecretRevoked | IDeviceSecretIssued,
  verb: "отозван" | "перевыпущен",
): string {
  const reason = closeReason(result);
  const action =
    verb === "перевыпущен"
      ? "Секрет перевыпущен — запишите его в изделие."
      : "Секрет отозван.";

  return (
    "Закрыть соединение не удалось — изделие может оставаться на связи " +
    `до переподключения. ${action}` +
    (reason ? ` Причина: ${reason}.` : "") +
    " Перезапустите изделие или повторите."
  );
}

function secretOutcomeMessage(
  result: IDeviceSecretRevoked | IDeviceSecretIssued,
  verb: "отозван" | "перевыпущен",
): string {
  const outcome = connectionOutcomeOf(result);

  if (outcome === "failed") return failedOutcomeMessage(result, verb);

  const text = outcomeByText(result, outcome);

  // Перевыпуск — это ещё и «запишите новый секрет в изделие»: без этой подсказки
  // сообщение об исходе закрытия остаётся без главного действия.
  return verb === "перевыпущен"
    ? `секрет перевыпущен — запишите его в изделие; ${text}`
    : `секрет ${verb}; ${text}`;
}

/**
 * `DEV-97.1` (попутное `АУД-122`): отказ закрытия — **предупреждение без
 * автоскрытия**. Прежний тост гас через 5 секунд, а изделие в этот момент может
 * работать по старому секрету: сообщение, которое просят прочитать и среагировать,
 * само исчезать не должно. Два других исхода (`closed`, `not_connected`) — как
 * раньше: успех, который гаснет сам.
 */
export function outcomeToastProps(
  result: IDeviceSecretRevoked | IDeviceSecretIssued,
) {
  return connectionOutcomeOf(result) === "failed"
    ? {
        type: "warning" as const,
        autoClose: false as const,
        closeButton: true,
        closeOnClick: true,
        draggable: true,
      }
    : { type: "success" as const, ...RESULT_TOAST };
}

/**
 * DEV-78 (ПРВ-06, ПРВ-05): состояние и действия с секретом изделия для карточки.
 *
 * Значение секрета живёт только здесь и только до первого скрытия: сервер отдаёт
 * его один раз в ответе выдачи, повторного чтения нет. Признак выдачи
 * перечитывается после каждой операции.
 */
export function useModuleDeviceSecret({
  moduleId,
  enabled,
  chargeId = null,
  fetchStatus,
  issue,
  revoke,
}: UseModuleDeviceSecretOptions) {
  const [status, setStatus] = useState<IDeviceSecretStatus | null>(null);
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Функции приходят пропсами и меняют тождество на каждом рендере родителя;
  // держим их в ссылке, чтобы чтение признака не зациклилось на новом пропсе.
  const apiRef = useRef({ fetchStatus, issue, revoke });
  useEffect(() => {
    apiRef.current = { fetchStatus, issue, revoke };
  });

  const load = useCallback(async () => {
    const { fetchStatus: fetch } = apiRef.current;
    if (!enabled || !moduleId || !fetch) {
      setStatus(null);
      return;
    }

    try {
      setStatus(await fetch(moduleId));
    } catch {
      // Признак выдачи — не то, ради чего стоит сыпать ошибками в карточке.
      setStatus(null);
    }
  }, [enabled, moduleId]);

  useEffect(() => {
    // Новое изделие — прежнее показанное значение не показываем.
    setIssuedSecret(null);
    load();
  }, [load]);

  const confirm = (params: {
    message: string;
    confirmTitle: string;
    /** `п. 1`: подтверждение вводом (перевыпуск называет изделие). */
    confirmInput?: ConfirmInput;
    action: (typed?: string) => Promise<void>;
  }) => {
    const toastId = `device-secret-${moduleId}`;

    toast(
      <ConfirmToast
        message={params.message}
        confirmInput={params.confirmInput}
        onConfirm={async (typed?: string) => {
          try {
            setIsLoading(true);
            await params.action(typed);
          } catch (error) {
            // `п. 1`: «секрет уже выдан» — не ошибка ввода, а повод подтвердить
            // перевыпуск. Признак перечитываем: карточка узнаёт, что секрет есть,
            // и следующее нажатие открывает окно подтверждения.
            const conflict = isConflict(error);

            if (conflict) {
              await load();
            }

            toast.update(toastId, {
              render: conflict
                ? REISSUE_REQUIRED_MESSAGE
                : errorMessage(error, "Операция с секретом не выполнена"),
              type: conflict ? "warning" : "error",
              ...RESULT_TOAST,
            });
          } finally {
            setIsLoading(false);
          }
        }}
        onCancel={() => undefined}
        title={params.confirmTitle}
        toastId={toastId}
        closeOnConfirm={false}
      />,
      {
        toastId,
        style: { borderRadius: 0 },
        progressClassName: "toast-progress-delete",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      },
    );
  };

  const handleIssue = () => {
    if (!enabled || !moduleId || !apiRef.current.issue) {
      toast.error("Функция выдачи секрета не передана");
      return;
    }

    // `п. 1` резерва (пара к п. 19): если секрет уже есть, это **перевыпуск** —
    // он обесценивает текущий секрет изделия, поэтому подтверждается вводом
    // идентификатора изделия и уходит с `replace: true`. Молчаливой замены нет.
    const isReissue = Boolean(status?.provisioned);

    confirm({
      message: isReissue
        ? "Секрет уже выдан. Перевыпуск обесценит текущий: изделие потеряет связь, пока не получит новый секрет. Введите идентификатор изделия, чтобы подтвердить."
        : "Выдать секрет изделия?",
      confirmTitle: isReissue ? "перевыпустить" : "выдать",
      confirmInput:
        isReissue && chargeId
          ? {
              label: `идентификатор изделия (${chargeId})`,
              placeholder: chargeId,
              expected: chargeId,
            }
          : undefined,
      action: async (typed?: string) => {
        // Подтверждение вводом: значение обязано совпасть с идентификатором.
        // Кнопка недоступна до совпадения, но проверяем и здесь — диалог может
        // быть вызван не мышью, и «да» без ввода не должно пройти.
        if (isReissue && chargeId && typed !== chargeId) {
          throw new Error(
            "подтверждение не совпало с идентификатором изделия — перевыпуск не выполнен",
          );
        }

        // Первая выдача зовётся без замены: `replace` появляется только там, где
        // замена действительно названа, — иначе признак теряет смысл.
        const result = isReissue
          ? await apiRef.current.issue!(moduleId, { replace: true })
          : await apiRef.current.issue!(moduleId);
        // Показываем ровно то, что вернул сервер, — без перекодирования.
        setIssuedSecret(result.secret);
        await load();
        toast.update(`device-secret-${moduleId}`, {
          // `DEV-97.1`: перевыпуск закрывает соединение, открытое по прежнему
          // секрету, — исход называем. Первая выдача закрывать нечего: изделие с
          // этим секретом ещё не соединялось, и сообщение остаётся прежним.
          render: result.replaced
            ? issueResultMessage(result)
            : "Секрет выдан — запишите его в изделие",
          // `DEV-97.1` (попутное `АУД-122`): отказ закрытия — предупреждение без
          // автоскрытия; первая выдача — прежний успех, закрывать было нечего.
          ...(result.replaced
            ? outcomeToastProps(result)
            : { type: "success" as const, ...RESULT_TOAST }),
        });
      },
    });
  };

  const handleRevoke = () => {
    if (!enabled || !moduleId || !apiRef.current.revoke) {
      toast.error("Функция отзыва секрета не передана");
      return;
    }

    confirm({
      message:
        "Отозвать секрет изделия? Изделие потеряет связь, пока не получит новый секрет.",
      confirmTitle: "отозвать",
      action: async () => {
        // `DEV-97`: исход отзыва называет, закрылось ли соединение изделия.
        const result = await apiRef.current.revoke!(moduleId);
        setIssuedSecret(null);
        await load();
        toast.update(`device-secret-${moduleId}`, {
          render: revokeResultMessage(result),
          // `DEV-97.1` (попутное `АУД-122`): отказ закрытия — предупреждение,
          // которое не гаснет само.
          ...outcomeToastProps(result),
        });
      },
    });
  };

  return {
    status,
    issuedSecret,
    isLoading,
    handleIssue,
    handleRevoke,
    hideIssuedSecret: () => setIssuedSecret(null),
  };
}
